import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { StrengthBadge } from "../components/Badges";
import { Plus, Building2, Pencil, Sparkles, ArrowRight, FileText, Clock, Loader2, Radar, Search } from "lucide-react";

function IntelCard({ navigate }) {
  const [intel, setIntel] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/business/intel");
        if (stop) return;
        setIntel(data);
        if (data.status === "researching") setTimeout(load, 5000);
      } catch { setIntel({ status: "none" }); }
    };
    load();
    return () => { stop = true; };
  }, []);
  const status = intel?.status;
  return (
    <button onClick={() => navigate("/intel")} data-testid="intel-card"
      className="text-left bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Radar className="w-4.5 h-4.5 text-slate-900" /></div>
          <h3 className="font-semibold text-slate-900">Business Intelligence</h3>
        </div>
        {status === "researching" && <span className="flex items-center gap-1.5 text-xs text-blue-600"><Loader2 className="w-3 h-3 animate-spin" /> Researching</span>}
        {status === "complete" && <span className="text-xs text-emerald-600 font-mono">Ready</span>}
      </div>
      <p className="text-sm text-slate-500">
        {status === "complete" ? "Your cached digital-footprint profile is ready and powering every analysis."
          : status === "researching" ? "Compiling your public footprint from real sources…"
          : "Research your full public footprint — website, social, reviews, news, competitors."}
      </p>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 mt-4">
        {status === "complete" ? "View profile" : status === "researching" ? "View progress" : "Run deep research"} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

function ProfileCard({ business, navigate }) {
  if (!business) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <Building2 className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-slate-900">No business profile yet</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Create your profile so every analysis is tailored to your business.</p>
        <Button onClick={() => navigate("/setup")} data-testid="create-profile-button" className="bg-slate-900 hover:bg-slate-800 rounded-lg">Create profile</Button>
      </div>
    );
  }
  const rows = [
    ["Industry", business.industry], ["Region", business.country],
    ["Model", business.business_model], ["Size", business.company_size],
    ["Stage", business.stage], ["Main segment", business.main_segment],
  ].filter(([, v]) => v);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="business-summary">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-lg leading-tight">{business.name}</h3>
            <p className="text-xs text-slate-500">{business.industry}</p>
          </div>
        </div>
        <button onClick={() => navigate("/setup")} data-testid="edit-profile-button" className="text-slate-400 hover:text-slate-900 transition-colors">
          <Pencil className="w-4 h-4" />
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">{k}</dt>
            <dd className="text-sm text-slate-800 font-medium truncate">{v}</dd>
          </div>
        ))}
      </dl>
      {business.business_goal && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <dt className="text-[10px] uppercase tracking-wider text-slate-400">Current goal</dt>
          <dd className="text-sm text-slate-700 mt-1">{business.business_goal}</dd>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { business, loadBusiness } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/decisions");
      setDecisions(data);
    } catch (e) { console.error("Failed to load decisions", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/demo/seed");
      await loadBusiness();
      toast.success("Demo loaded");
      navigate(`/decisions/${data.decision_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSeeding(false); }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Research decisions with real-world evidence.</p>
        </div>
        <Button variant="outline" onClick={runDemo} disabled={seeding} data-testid="load-demo-button" className="rounded-lg gap-2">
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Load demo analysis
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1"><ProfileCard business={business} navigate={navigate} /></div>

        <button onClick={() => navigate("/decisions/new")} data-testid="analyze-decision-cta"
          className="lg:col-span-2 group text-left bg-slate-900 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-[0.08]" />
          <div className="relative">
            <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center mb-5">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold text-white tracking-tight">Analyze a Decision</h2>
            <p className="text-slate-300 mt-2 max-w-md">Enter a decision you're weighing. We'll find comparable businesses that made a similar move and show what happened.</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white mt-6 group-hover:gap-2.5 transition-all">
              Start analysis <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </button>
      </div>

      {business && <div className="mb-8"><IntelCard navigate={navigate} /></div>}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-semibold text-slate-900">Recent decisions</h2>
        {decisions.length > 0 && (
          <button onClick={() => navigate("/history")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-12 text-center">Loading…</div>
      ) : decisions.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No decisions yet. Analyze your first decision or load the demo.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {decisions.slice(0, 6).map((d) => (
            <button key={d.id} onClick={() => navigate(`/decisions/${d.id}`)} data-testid={`decision-card-${d.id}`}
              className="text-left bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-600">{d.category}</span>
                {d.is_demo && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">DEMO</span>}
              </div>
              <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2 mb-3">{d.title}</h3>
              <div className="flex items-center justify-between">
                {d.status === "analyzing" ? (
                  <span className="flex items-center gap-1.5 text-xs text-blue-600"><Loader2 className="w-3 h-3 animate-spin" /> Analyzing</span>
                ) : d.status === "error" ? (
                  <span className="text-xs text-rose-600">Failed</span>
                ) : (
                  <StrengthBadge value={d.evidence_strength} testid={`strength-${d.id}`} />
                )}
                <span className="text-xs text-slate-400 font-mono">{d.cases_count} cases</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </AppShell>
  );
}
